import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { motion } from 'framer-motion';
import { CheckCircle, Edit3, Loader2, Save, X } from 'lucide-react';
import { useMemo, useState } from 'react';

interface SectionField {
  key: string;
  label: string;
  value: string;
  placeholder?: string;
  onClick?: (event: React.MouseEvent) => void; // New prop for field click handling
}

interface EditableSectionCardProps {
  title: string;
  fields: SectionField[];
  displayFields?: SectionField[]; // Optional display fields for compact view
  isLoading?: boolean;
  error?: string | null;
  index?: number;
  onUpdate: (updatedFields: Record<string, string>) => Promise<void>;
  onAccept?: () => void;
}

const EditableSectionCard = ({
  title,
  fields,
  displayFields,
  isLoading,
  error = null,
  index = 0,
  onUpdate,
  onAccept,
}: EditableSectionCardProps) => {
  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);
  const [editValues, setEditValues] = useState<Record<string, string>>({});
  const [isUpdating, setIsUpdating] = useState(false);

  // Check if there's actual data available (not just empty fields)
  const hasData = useMemo(() => {
    if (error === 'Data not found') return false;
    if (isLoading) return false;

    const fieldsToCheck = displayFields || fields;
    return fieldsToCheck.some(
      (field) => field.value && field.value.trim() !== ''
    );
  }, [error, isLoading, fields, displayFields]);

  const handleEditClick = () => {
    // Initialize edit values with current field values
    const initialValues = fields.reduce((acc, field) => {
      acc[field.key] = field.value;
      return acc;
    }, {} as Record<string, string>);

    setEditValues(initialValues);
    setIsEditDialogOpen(true);
  };

  const handleSaveEdit = async () => {
    setIsUpdating(true);
    try {
      await onUpdate(editValues);
      setIsEditDialogOpen(false);
    } catch (error) {
      console.error('Failed to update section:', error);
      // You might want to show a toast notification here
    } finally {
      setIsUpdating(false);
    }
  };

  const handleCancelEdit = () => {
    setIsEditDialogOpen(false);
    setEditValues({});
  };

  return (
    <>
      <motion.div
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: index * 0.1, duration: 0.4, ease: 'easeOut' }}
        className="group">
        {/* Main card */}
        <div className="relative bg-white/70 dark:bg-slate-900/25 backdrop-blur-lg border border-slate-300/20 dark:border-white/20 rounded-2xl p-4 shadow-md hover:shadow-lg transition-all duration-300">
          {/* Inner highlight */}
          <div className="absolute inset-[1px] rounded-2xl bg-gradient-to-t from-transparent via-transparent to-white/20 dark:to-white/8 pointer-events-none" />

          {/* Content */}
          <div className="relative z-10">
            {/* Header */}
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center gap-2">
                <h3 className="text-slate-800 dark:text-slate-200 font-semibold text-lg tracking-wide">
                  {title}
                </h3>
                {isLoading && (
                  <motion.div
                    initial={{ opacity: 0, scale: 0.8 }}
                    animate={{ opacity: 1, scale: 1 }}
                    className="flex items-center gap-1 ml-2">
                    <motion.div
                      animate={{ rotate: 360 }}
                      transition={{
                        duration: 1.2,
                        repeat: Infinity,
                        ease: 'linear',
                      }}>
                      <Loader2 className="w-4 h-4 text-eyevantage-primary" />
                    </motion.div>
                    <motion.span
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      transition={{ delay: 0.2 }}
                      className="text-sm text-slate-500 dark:text-slate-400">
                      Loading...
                    </motion.span>
                  </motion.div>
                )}
              </div>
              {hasData && (
                <div className="flex items-center gap-2">
                  <Button
                    onClick={(e) => {
                      e.stopPropagation();
                      handleEditClick();
                    }}
                    size="sm"
                    variant="outline"
                    className="h-8 px-3 bg-slate-100/80 hover:bg-white dark:bg-slate-800/80 dark:hover:bg-slate-700/90 border border-gray-400/40 dark:border-white/20 text-slate-700 dark:text-slate-300 rounded-2xl text-sm font-medium transition-all duration-300 backdrop-blur-sm shadow-sm"
                    disabled={isLoading}>
                    <Edit3 className="w-3 h-3 mr-1" />
                    Edit
                  </Button>
                  {onAccept && (
                    <Button
                      onClick={(e) => {
                        e.stopPropagation();
                        onAccept();
                      }}
                      size="sm"
                      className="h-8 px-3 bg-emerald-100/50 hover:bg-white dark:bg-slate-800/80 dark:hover:bg-slate-700/90 border border-emerald-600/60 dark:border-white/20 text-emerald-700 dark:text-emerald-700 rounded-2xl text-sm font-medium transition-all duration-300 backdrop-blur-sm shadow-sm"
                      disabled={isLoading}>
                      <CheckCircle className="w-3 h-3 mr-1" />
                      Accept
                    </Button>
                  )}
                </div>
              )}
            </div>

            {/* Content */}
            <div className="space-y-4">
              {error ? (
                <div className="text-slate-500 dark:text-slate-400 text-sm italic">
                  {error === 'Data not found' ? (
                    `No ${title.toLowerCase()} data available yet`
                  ) : (
                    <div className="text-red-600 dark:text-red-400">
                      <span className="font-medium">Error:</span> {error}
                    </div>
                  )}
                </div>
              ) : isLoading ? (
                <motion.div
                  initial={{ opacity: 0, y: 5 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="flex items-center gap-2 text-slate-500 dark:text-slate-400 text-md">
                  <motion.div
                    animate={{ rotate: 360 }}
                    transition={{
                      duration: 1.5,
                      repeat: Infinity,
                      ease: 'linear',
                    }}>
                    <Loader2 className="w-4 h-4" />
                  </motion.div>
                  <motion.span
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ delay: 0.3 }}>
                    Fetching section content...
                  </motion.span>
                </motion.div>
              ) : (
                (displayFields || fields).map((field) => (
                  <div key={field.key} className="space-y-2">
                    <div className="flex items-center gap-2">
                      <h4 className="text-slate-700 dark:text-slate-300 text-md font-semibold">
                        {field.label}
                      </h4>
                    </div>
                    {field.value ? (
                      <div
                        onClick={field.onClick}
                        className={`text-slate-700 dark:text-slate-300 bg-slate-100 dark:bg-slate-800 rounded-lg p-2 leading-relaxed whitespace-pre-line text-md ${
                          field.onClick
                            ? 'cursor-pointer hover:bg-slate-100 dark:hover:bg-slate-800 rounded-lg p-2 transition-all duration-200 group'
                            : ''
                        }`}>
                        <p
                          className={
                            field.onClick
                              ? 'font-medium transition-all duration-200'
                              : ''
                          }>
                          {field.value}
                        </p>
                        {field.onClick && (
                          <div className="opacity-0 group-hover:opacity-100 transition-opacity duration-200 mt-1">
                            <span className="text-xs text-amber-600 dark:text-amber-400 font-medium">
                              Click to highlight related transcript segments
                            </span>
                          </div>
                        )}
                      </div>
                    ) : (
                      <div className="text-slate-500 dark:text-slate-400 text-sm italic ml-3">
                        No content available yet
                      </div>
                    )}
                  </div>
                ))
              )}
            </div>
          </div>
        </div>
      </motion.div>

      {/* Edit Dialog */}
      <Dialog open={isEditDialogOpen} onOpenChange={setIsEditDialogOpen}>
        <DialogContent
          className="max-w-4xl max-h-[80vh] overflow-y-auto bg-white/95 dark:bg-slate-900/95 backdrop-blur-lg border border-white/30 dark:border-white/15"
          onClick={(e) => e.stopPropagation()}>
          <DialogHeader>
            <DialogTitle className="text-slate-800 dark:text-slate-200 font-semibold text-xl">
              Edit {title}
            </DialogTitle>
          </DialogHeader>

          <div className="space-y-4 py-4">
            {fields.map((field) => (
              <div key={field.key} className="space-y-2">
                <Label
                  htmlFor={field.key}
                  className="text-slate-700 dark:text-slate-300 font-medium text-md">
                  {field.label}
                </Label>
                <Textarea
                  id={field.key}
                  value={editValues[field.key] || ''}
                  onChange={(e) =>
                    setEditValues((prev) => ({
                      ...prev,
                      [field.key]: e.target.value,
                    }))
                  }
                  placeholder={
                    field.placeholder || `Enter ${field.label.toLowerCase()}`
                  }
                  className="min-h-24 resize-none bg-white/70 dark:bg-slate-800/70 border border-gray-400/40 dark:border-white/15 focus:border-eyevantage-primary/50 dark:focus:border-eyevantage-secondary/50"
                  rows={4}
                />
              </div>
            ))}
          </div>

          <DialogFooter className="gap-2">
            <Button
              variant="outline"
              onClick={(e) => {
                e.stopPropagation();
                handleCancelEdit();
              }}
              disabled={isUpdating}
              className="rounded-3xl bg-white/80 dark:bg-slate-800/80 border border-gray-400/40 dark:border-white/20">
              <X className="w-4 h-4 mr-2" />
              Cancel
            </Button>
            <Button
              onClick={(e) => {
                e.stopPropagation();
                handleSaveEdit();
              }}
              disabled={isUpdating}
              className="rounded-3xl bg-gradient-to-r from-purple-500 to-blue-800 hover:from-purple-500 hover:to-blue-800 text-white">
              {isUpdating ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Saving...
                </>
              ) : (
                <>
                  <Save className="w-4 h-4 mr-2" />
                  Save Changes
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default EditableSectionCard;
